import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import TopHeader from '../components/TopHeader';
import SantralPanel from '../components/SantralPanel';
import LogoutConfirmDialog from '../components/LogoutConfirmDialog';
import DevZoneFooter from '../components/DevZoneFooter';
import { isSystemDevZonePath } from '../constants/systemDevZone';
import { apiRequest } from '../services/apiClient';

const DESKTOP_SIDEBAR_COLLAPSED_KEY = 'hlm-ap-dashboard-sidebar-collapsed';
const SANTRAL_PANEL_OPEN_KEY = 'hlm-ap-santral-panel-open';

/** Dahili atanabilen (softphone kullanabilen) roller */
const SANTRAL_ROLLERI = ['admin', 'mod', 'sales'];
const santralRolUygun = (user) => SANTRAL_ROLLERI.includes(String(user?.rol || '').toLowerCase());
const isAdminRol = (user) => String(user?.rol || '').toLowerCase() === 'admin';

/**
 * Dashboard layout component that keeps the header and sidebar fixed
 * while rendering dynamic page content inside the main area.
 */
const DashboardLayout = ({ currentUser, onLogout, activePage, onNavigate, children, user }) => {
  const { pathname } = useLocation();
  const showDevZoneFooter = isSystemDevZonePath(pathname);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isTabletDrawerOpen, setIsTabletDrawerOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(() => {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem(DESKTOP_SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [santralOpen, setSantralOpen] = useState(() => {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem(SANTRAL_PANEL_OPEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [santralHasIncoming, setSantralHasIncoming] = useState(false);

  // Santral paneli kullanıcıya bir dahili atanmışsa tam (softphone) modda gösterilir
  // (sales personeli + dahili atanmış admin/mod kullanıcıları dahil).
  // Dahili atanmamış admin'ler ise paneli salt-izleme modunda görür: yalnızca
  // anlık kuyruk akışı; müsaitlik / bağlan / bağlantıyı kes / cevapla yok.
  const [showSantralPanel, setShowSantralPanel] = useState(false);
  const [santralIzleme, setSantralIzleme] = useState(false);

  useEffect(() => {
    let iptal = false;
    if (!santralRolUygun(user)) {
      setShowSantralPanel(false);
      setSantralIzleme(false);
      return undefined;
    }
    apiRequest('/api/santral/personel/durum/benim', { method: 'GET', skipCsrf: true })
      .then((r) => {
        if (iptal) return;
        const atanmis = r?.veri?.atanmisDahiliVar === true;
        if (atanmis) {
          setShowSantralPanel(true);
          setSantralIzleme(false);
        } else if (isAdminRol(user)) {
          setShowSantralPanel(true);
          setSantralIzleme(true);
        } else {
          setShowSantralPanel(false);
          setSantralIzleme(false);
        }
      })
      .catch(() => {
        if (iptal) return;
        // Atama bilgisi alınamasa da admin paneli izleme modunda görebilsin
        if (isAdminRol(user)) {
          setShowSantralPanel(true);
          setSantralIzleme(true);
        } else {
          setShowSantralPanel(false);
          setSantralIzleme(false);
        }
      });
    return () => {
      iptal = true;
    };
  }, [user]);

  useEffect(() => {
    try {
      localStorage.setItem(SANTRAL_PANEL_OPEN_KEY, santralOpen ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [santralOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(DESKTOP_SIDEBAR_COLLAPSED_KEY, desktopSidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [desktopSidebarCollapsed]);

  /** Dashboard: body scroll yerine content-area scroll — iPad Safari nested scroll odağı */
  useEffect(() => {
    document.body.classList.add('ap-dashboard-shell');
    return () => document.body.classList.remove('ap-dashboard-shell');
  }, []);

  const closeSidebar = () => setSidebarOpen(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Close sidebar on window resize if going from mobile to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) {
        closeSidebar();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar when clicking outside (on mobile)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (window.innerWidth <= 1024 && sidebarOpen) {
        const sidebar = document.getElementById('sidebar');
        const handle = document.getElementById('sidebarToggleHandle');

        if (sidebar && handle) {
          const isClickInsideSidebar = sidebar.contains(e.target);
          const isClickOnHandle = handle.contains(e.target);

          if (!isClickInsideSidebar && !isClickOnHandle) {
            closeSidebar();
          }
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [sidebarOpen]);

  /** Tablet drawer açıkken arka plan kaydırmasını kilitle (iOS Safari scroll bleed) */
  useEffect(() => {
    const apply = () => {
      const open = sidebarOpen && window.innerWidth <= 1024;
      setIsTabletDrawerOpen(open);
      document.documentElement.classList.toggle('sidebar-drawer-open', open);
      document.body.classList.toggle('sidebar-drawer-open', open);
    };
    apply();
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      document.documentElement.classList.remove('sidebar-drawer-open');
      document.body.classList.remove('sidebar-drawer-open');
    };
  }, [sidebarOpen]);

  /** Drawer açıkken sidebar dışındaki touch kaydırmasını engelle */
  useEffect(() => {
    if (!sidebarOpen) return undefined;

    const onTouchMove = (e) => {
      if (window.innerWidth > 1024) return;
      const sidebar = document.getElementById('sidebar');
      if (sidebar?.contains(e.target)) return;
      e.preventDefault();
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => document.removeEventListener('touchmove', onTouchMove);
  }, [sidebarOpen]);

  const layoutInner = (
    <div
      className={`dashboard-layout${desktopSidebarCollapsed ? ' dashboard-layout--sidebar-collapsed' : ''}${isTabletDrawerOpen ? ' dashboard-layout--sidebar-drawer-open' : ''}${showSantralPanel && santralOpen ? ' dashboard-layout--santral-open' : ''}`}
    >

      <div
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={closeSidebar}
      ></div>

      <button
        className={`sidebar-toggle-handle ${sidebarOpen ? 'active' : ''}`}
        id="sidebarToggleHandle"
        onClick={(e) => {
          e.stopPropagation();
          toggleSidebar();
        }}
        aria-label="Toggle sidebar"
        type="button"
      >
        <i className="fas fa-chevron-right handle-icon"></i>
      </button>

      <button
        type="button"
        className="sidebar-desktop-expand-handle"
        onClick={() => setDesktopSidebarCollapsed(false)}
        aria-label="Kenar çubuğunu aç"
        title="Menüyü aç"
      >
        <i className="fas fa-angle-double-right" aria-hidden />
      </button>

      <div className="main-container">
        <Sidebar
          isOpen={sidebarOpen}
          currentUser={currentUser}
          onLogout={() => setLogoutConfirmOpen(true)}
          activePage={activePage}
          onNavigate={onNavigate}
          user={user}
        />

        <div className="content-wrapper-with-header">
          <TopHeader
            desktopSidebarCollapsed={desktopSidebarCollapsed}
            onToggleDesktopSidebar={() => setDesktopSidebarCollapsed((v) => !v)}
            showSantralToggle={showSantralPanel}
            santralOpen={santralOpen}
            onToggleSantral={() => setSantralOpen((v) => !v)}
            santralHasIncoming={santralHasIncoming}
          />
          <div
            className={`content-area${showDevZoneFooter ? ' content-area--dev-zone' : ''}`}
          >
            {children}
            <DevZoneFooter />
          </div>
        </div>
      </div>

      {showSantralPanel ? (
        <SantralPanel
          isOpen={santralOpen}
          izleme={santralIzleme}
          onToggle={() => setSantralOpen((v) => !v)}
          onClose={() => setSantralOpen(false)}
          onIncomingChange={setSantralHasIncoming}
        />
      ) : null}

      <LogoutConfirmDialog
        open={logoutConfirmOpen}
        onCancel={() => setLogoutConfirmOpen(false)}
        onConfirm={async () => {
          await onLogout?.();
          setLogoutConfirmOpen(false);
        }}
      />
    </div>
  );

  return layoutInner;
};

export default DashboardLayout;


